import React, { Fragment, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { selectGiftCardsOrderCards } from '@selectors/giftCards';
import {
  removeGiftCard,
  updateGiftCardPersonalization,
} from '@actions/giftCards';

import { numberToSup } from '@utils/formatNumber';

import { BUTTON_VARIANTS } from '@common/components/Button';
import DeleteButton from '@common/components/DeleteButton';
import ConfirmationDialog from '@common/components/Modal/ConfirmationDialog';
import Modal from '@common/components/Modal';

import PersonalizationModal from './PersonalizationModal';
import {
  OrderList,
  OrderItem,
  OrderItemValue,
  OrderItemDedicationButton,
  OrderItemRemoveButtonWrapper,
} from './Order.styled';

const Order = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const giftCards = useSelector(selectGiftCardsOrderCards);
  const [modalPersonalizationId, setModalPersonalizationId] = useState(null);
  const [isConfirmDeleteOpenId, setIsConfirmDeleteOpenId] = useState(null);

  const giftCardsIds = Object.keys(giftCards);

  const handlePersonalizationModalClose = (giftCardId, personalization) => {
    dispatch(
      updateGiftCardPersonalization({
        id: giftCardId,
        personalization,
      }),
    );
    setModalPersonalizationId(null);
  };

  if (!giftCardsIds.length) {
    return null;
  }

  return (
    <Fragment>
      <OrderList>
        {giftCardsIds.map(id => {
          const isPersonalized = Object.values(
            giftCards[id].personalization,
          ).some(val => val);

          return (
            <OrderItem key={id}>
              <OrderItemValue>
                {t(
                  'giftCards.order.cards.entry.text',
                  'CHF {{value}} Gutschein',
                  {
                    value: numberToSup(giftCards[id].value),
                  },
                )}
              </OrderItemValue>

              <OrderItemDedicationButton
                variant={BUTTON_VARIANTS.LEVEL_2_BLUEGREY_90_SMALL}
                onClick={() => {
                  setModalPersonalizationId(id);
                }}
              >
                {isPersonalized
                  ? t(
                      'giftCards.order.cards.entry.buttonEdit',
                      'Widmung anpassen',
                    )
                  : t(
                      'giftCards.order.cards.entry.buttonAdd',
                      'Widmung hinzufügen',
                    )}
              </OrderItemDedicationButton>

              <OrderItemRemoveButtonWrapper>
                <DeleteButton
                  onClick={() => {
                    if (isPersonalized) {
                      return setIsConfirmDeleteOpenId(id);
                    }
                    dispatch(removeGiftCard(id));
                  }}
                />
              </OrderItemRemoveButtonWrapper>
            </OrderItem>
          );
        })}
      </OrderList>

      <Modal
        isOpen={isConfirmDeleteOpenId}
        onClose={() => isConfirmDeleteOpenId(null)}
        withCloseButton={false}
        mountOnEnter
        unmountOnExit
        appear
        exit
        enter
        timeout={500}
      >
        <ConfirmationDialog
          onDismiss={() => setIsConfirmDeleteOpenId(null)}
          onConfirm={() => {
            dispatch(removeGiftCard(isConfirmDeleteOpenId));
            setIsConfirmDeleteOpenId(null);
          }}
          title={t(
            'giftCards.order.dedicationRemoveModal.title',
            'Änderungen verwerfen??',
          )}
          description={t(
            'giftCards.order.dedicationRemoveModal.description',
            'Möchtest Du Deine ungespeicherten Änderungen wirklich verwerfen?',
          )}
          dismissText={t(
            'giftCards.order.dedicationRemoveModal.dismiss',
            'Abbrechen',
          )}
          confirmText={t(
            'giftCards.order.dedicationRemoveModal.confirm',
            'Verwerfen',
          )}
        />
      </Modal>

      <PersonalizationModal
        giftCardId={modalPersonalizationId}
        personalization={giftCards?.[modalPersonalizationId]?.personalization}
        onClose={handlePersonalizationModalClose}
        onDiscard={() => {
          setModalPersonalizationId(null);
        }}
      />
    </Fragment>
  );
};

export default Order;
